import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { T, useTranslate } from '@tolgee/react';
import { TrackingOrder } from '../orderTracking.interface';
import {
	TRACKING_DRAWER_TRANSITION,
	TRACKING_STATUS_COLOR_CLASSES,
} from '../orderTracking.constants';
import BuyerStepsList from './BuyerStepsList';
import { ChevronDownIcon } from '../../../../assets/ChevronDownIcon';
import { StatusColors } from '../../../../constants/order-status.constants';
import { cn } from '../../../../lib/utils';

interface OrderInfoSectionProps {
	order: TrackingOrder;
}

const InfoRow: React.FC<{ label: React.ReactNode; value: React.ReactNode }> = ({
	label,
	value,
}) => (
	<div className="flex w-full flex-col gap-1 sm:flex-row sm:items-center sm:gap-4 py-[5px] text-sm">
		<div className="sm:w-[170px] flex-none text-track-muted">{label}</div>
		<div className="w-full min-w-0 font-medium text-track-ink">{value}</div>
	</div>
);

const OrderInfoSection: React.FC<OrderInfoSectionProps> = ({ order }) => {
	const { t } = useTranslate();
	const [showSteps, setShowSteps] = useState<boolean>(false);
	const platformStatus = StatusColors[order.status];
	const statusLabel = platformStatus
		? t(platformStatus.label, order.status)
		: order.status.replace(/_/g, ' ');
	const statusClassName =
		TRACKING_STATUS_COLOR_CLASSES[platformStatus?.color ?? 'gray'];
	const percent = order.orderedQuantity
		? Math.min(100, (order.loadedQuantity / order.orderedQuantity) * 100)
		: 0;
	const productType = [order.productType, order.productSize]
		.filter(Boolean)
		.join(' · ');

	return (
		<div className="flex flex-col">
			<InfoRow
				label={<T keyName="orderTracking.orderStatus">Order status:</T>}
				value={
					<div className="flex items-center justify-between gap-3 flex-wrap">
						<span
							className={cn(
								'inline-flex items-center h-[26px] px-3 rounded-full text-[12.5px] font-semibold whitespace-nowrap',
								statusClassName,
							)}
						>
							{statusLabel}
						</span>
						<button
							type="button"
							onClick={() => setShowSteps((value) => !value)}
							className="flex items-center gap-[6px] border-none bg-transparent p-0 font-[inherit] text-[13px] font-semibold text-track-link cursor-pointer transition hover:text-track-link-hover"
						>
							{showSteps ? (
								<T keyName="orderStepper.viewLess">View less</T>
							) : (
								<T keyName="orderStepper.viewAllSteps">View all steps</T>
							)}
							<span
								className={cn(
									'flex transition-transform duration-300 ease-[cubic-bezier(0.2,0,0.2,1)]',
									showSteps && 'rotate-180',
								)}
							>
								<ChevronDownIcon color="#5080F8" width={10} height={6} />
							</span>
						</button>
					</div>
				}
			/>
			<AnimatePresence initial={false}>
				{showSteps ? (
					<motion.div
						key="buyer-steps"
						initial={{ height: 0, opacity: 0 }}
						animate={{ height: 'auto', opacity: 1 }}
						exit={{ height: 0, opacity: 0 }}
						transition={TRACKING_DRAWER_TRANSITION}
						className="overflow-hidden"
					>
						<BuyerStepsList
							steps={order.buyerSteps}
							current={order.buyerCurrentStep}
						/>
					</motion.div>
				) : null}
			</AnimatePresence>
			{order.contractNumber ? (
				<InfoRow
					label={<T keyName="orderTracking.contract">Contract:</T>}
					value={`#${order.contractNumber}`}
				/>
			) : null}

			<div className="border-0 border-t border-dashed border-track-line-strong my-3" />

			<InfoRow
				label={<T keyName="orderTracking.product">Product:</T>}
				value={order.productName}
			/>
			{productType ? (
				<InfoRow
					label={<T keyName="orderTracking.productType">Type:</T>}
					value={productType}
				/>
			) : null}
			<InfoRow
				label={<T keyName="orderTracking.orderedQuantity">Ordered quantity:</T>}
				value={`${order.orderedQuantity} ${order.unit}`}
			/>
			<div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4 py-[5px] text-sm">
				<div className="sm:w-[170px] flex-none text-track-muted">
					<T keyName="orderTracking.loadedQuantity">Loaded quantity:</T>
				</div>
				<div className="flex-1 min-w-0 flex items-center gap-3">
					<div className="flex-1 h-[10px] rounded-full bg-track-surface overflow-hidden">
						<i
							className="block h-full rounded-full bg-track-indigo transition-[width] duration-500"
							style={{ width: `${percent}%` }}
						/>
					</div>
					<span className="font-semibold whitespace-nowrap text-track-ink">
						{order.loadedQuantity} / {order.orderedQuantity} {order.unit}
					</span>
				</div>
			</div>
			{order.deliveryMode === 'multiple' ? (
				<InfoRow
					label={<T keyName="orderTracking.deliveries">Deliveries:</T>}
					value={
						<T
							keyName="orderTracking.deliveriesOf"
							params={{
								done: order.deliveriesDone,
								total: order.deliveriesTotal,
							}}
						>
							{'{done} of {total}'}
						</T>
					}
				/>
			) : null}
			{order.distanceKm ? (
				<InfoRow
					label={<T keyName="orderTracking.distance">Distance:</T>}
					value={`${Math.round(order.distanceKm)} km`}
				/>
			) : null}
		</div>
	);
};

export default OrderInfoSection;
